import React, { useEffect, useState } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { deleteData } from "../../api/AddressApi";

import {
  tbodyBorder,
  theadBackground,
  theadBorder,
  theadText,
} from "../../components/common/ColorTheme";
import { AddCircleSharp } from "@mui/icons-material";

import { toast } from "react-toastify";
import { showConfirmation } from "../../utils/SwalHelper";
import { AddressData, initialAddressData } from "../../types/Address";
import AddressForm from "./AddressForm";
import { CountyData } from "../../types/County";
import { CountryData } from "../../types/Country";
import { getData as getCountryList } from "../../api/CountryApi";
import { getData as getCountyList } from "../../api/CountyApi";
import { createData, getData, updateData } from "../../api/AddressApi";
import { UserData } from "../../types/User";

type ComponentProps = {
  detailData: UserData;
};

const AddressList: React.FC<ComponentProps> = ({ detailData }) => {
  const [inputData, setInputData] = useState<AddressData>(initialAddressData);
  const [listData, setListData] = useState<AddressData[]>([]);
  const [countyData, setCountyData] = useState<CountyData[]>([]);
  const [countryData, setCountryData] = useState<CountryData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadData = async () => {
    setLoading(true);

    const addresses = await getData(detailData.id);
    setListData(addresses);

    const countries = await getCountryList();
    if (countries == null) {
      toast("Failed to get coutries");
    }
    setCountryData(countries);

    const counties = await getCountyList();
    if (counties == null) {
      toast("Failed to get couties");
    }
    setCountyData(counties);

    setLoading(false);
  };

  const handleAdd = () => {
    setInputData(initialAddressData);
    setIsEdit(false);
    setOpen(true);
  };

  const handleEdit = async (data: AddressData) => {
    setIsEdit(true);

    await setInputData(data);

    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirmation(
        "Are you sure?",
        "Do you want to delete this record permanently?",
        "warning",
        "Yes, delete it!"
      );

      if (confirmed) {
        const resp = await deleteData(id);
        if (!resp) {
          toast.error("Failed to delete record");
          return;
        }
        toast.success("Record deleted successfully");
        await loadData();
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Error deleting record");
    }
  };

  const onSubmit = async (form: Partial<AddressData>) => {
    setIsEdit(false);
    // alert(JSON.stringify(form));
    if (form.id) {
      const resp = await updateData(form.id, form);
      if (resp == null) {
        toast.error("Failed to save address.");
        return;
      }

      setOpen(false);
      await loadData();
      toast.success("Address successfully updated");
    } else {
      // alert(JSON.stringify(form));
      const resp = await createData(form);
      if (resp == null) {
        toast.error("Failed to save address.");
        return;
      }
      setOpen(false);
      await loadData();
      toast.success("Address successfully created");
    }
  };

  const filteredData = listData.filter((data) => {
    const search = searchTerm.toLowerCase();
    return (
      (data.city?.toLowerCase().includes(search) ?? false) ||
      (data.street?.toLowerCase().includes(search) ?? false) ||
      (data.country.name?.toLowerCase().includes(search) ?? false) ||
      (data.county.name?.toLowerCase().includes(search) ?? false)
    );
  });

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div>
      <Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <TextField
            label="Search data"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300 }}
          />
          <Button startIcon={<AddCircleSharp />} onClick={handleAdd}></Button>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theadBackground,
                    "& th": {
                      color: theadText,
                      fontVariant: "small-caps",
                      borderRight: `1px solid ${theadBorder}`,
                      "&:last-child": { borderRight: "none" },
                    },
                  }}
                >
                  <TableCell align="left">Id</TableCell>
                  <TableCell align="left">Country</TableCell>
                  <TableCell align="left">County/Region</TableCell>
                  <TableCell align="left">City</TableCell>
                  <TableCell align="left">Street</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ backgroundColor: "#f5f5f5" }}>
                {filteredData.map((data) => (
                  <TableRow key={data.id}>
                    <TableCell
                      sx={{ borderRight: `1px solid ${tbodyBorder}` }}
                      align="left"
                    >
                      {data.id}
                    </TableCell>
                    <TableCell
                      sx={{ borderRight: `1px solid ${tbodyBorder}` }}
                      align="left"
                    >
                      {data.country.name}
                    </TableCell>
                    <TableCell
                      sx={{ borderRight: `1px solid ${tbodyBorder}` }}
                      align="left"
                    >
                      {data.county?.name ? data.county.name : data.region}
                    </TableCell>
                    <TableCell
                      sx={{ borderRight: `1px solid ${tbodyBorder}` }}
                      align="left"
                    >
                      {data.city}
                    </TableCell>
                    <TableCell
                      sx={{ borderRight: `1px solid ${tbodyBorder}` }}
                      align="left"
                    >
                      {data.street}
                    </TableCell>

                    <TableCell
                      sx={{ whiteSpace: "nowrap", borderRight: "none" }}
                      align="center"
                    >
                      <Tooltip title="Edit Person">
                        <IconButton
                          color="success"
                          onClick={() => handleEdit(data)}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Person">
                        <IconButton
                          color="error"
                          onClick={() => handleDelete(data.id)}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      No users found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog
          open={open}
          onClose={(event, reason) => {
            setInputData(initialAddressData);
            if (reason === "backdropClick" || reason === "escapeKeyDown")
              return;
            setOpen(false);
          }}
          disableEscapeKeyDown
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{isEdit ? "Edit Address" : "Add Address"}</DialogTitle>
          <DialogContent>
            <AddressForm
              onSubmit={onSubmit}
              detailData={detailData}
              initialData={inputData || initialAddressData}
              onClose={() => setOpen(false)}
              countyData={countyData}
              countryData={countryData}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </div>
  );
};

export default AddressList;
